import { Category } from '@modules/common/types';
import { TaxonomyActionType } from '@modules/taxonomy/store/taxonomyActionType';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import {
  Dialog,
  DialogContent,
  DialogProps,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import ModalHeader from '../Modal/ModalHeader';
import { CreateNewReasonModalTransitionComponent } from './CreateNewReasonModalTransitionComponent';
import { CreateNewReasonSuccessModal } from './CreateNewReasonSuccessModal';
import { CreateNewReasonV3ModalFooter } from './CreateNewReasonV3ModalFooter';
import { ReasonDescriptionGuide } from './ReasonDescriptionGuide';
import { ReasonDescriptionInput } from './ReasonDescriptionInput';
import { ReviewReason } from './ReviewReason';
import { trackCreateNewReasonEvents } from './helpers';

export interface CreateReasonSuccessCallback {
  reasonID: string;
  reasonName: string;
  reasonCategory: Category;
}

interface CreateNewReasonV3ModalProps extends Omit<DialogProps, 'onClose'> {
  onClose: () => void;
  selectedRC?: { name: string; category?: Category };
  feedbackID?: string;
  onCreateReason?: (opts: CreateReasonSuccessCallback) => void;
}

export const CreateNewReasonV3Modal = ({
  open,
  onClose,
  feedbackID,
  selectedRC,
  onCreateReason,
  ...modalProps
}: CreateNewReasonV3ModalProps) => {
  const [isCreateReasonSuccessModalOpen, setIsCreateReasonSuccessModalOpen] =
    useState(false);

  const dispatch = useTaxonomyStore(({ dispatch }) => dispatch);

  const {
    description,
    activeStep,
    selectedReasonDefinition,
    selectedFeedbackRecordID,
    isFetchingReasonDefinitions,
    onAfterCreateReason,
  } = useTaxonomyStore(
    ({ createReasonV3State: { description, activeStep, misc, visual } }) => ({
      description,
      activeStep,
      selectedFeedbackRecordID: misc?.selectedFeedbackRecordID,
      selectedReasonDefinition: misc?.selectedReasonDefinition,
      isFetchingReasonDefinitions: visual?.isFetchingReasonDefinitions,
      onAfterCreateReason: misc?.onAfterCreateReason,
    }),
    shallow,
  );

  const setDescription = (updatedDescription: string) => {
    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        description: updatedDescription,
      },
    });
  };

  const closeDialogAndResetState = () => {
    onClose();
    dispatch({
      type: TaxonomyActionType.RESET_CREATE_REASONV3_STATE,
    });
  };

  const handleDialogClose: DialogProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') {
      return;
    }

    closeDialogAndResetState();
  };

  const changeDescription = (description: string) => {
    setDescription(description);
  };

  const onMinimizeClick = () => {
    dispatch({
      type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
      payload: {
        visual: {
          isModalMinimised: true,
        },
      },
    });
    onClose();
    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Minimize CTA Clicked',
      description,
    });
  };

  const warnBeforeQuitting = useCallback(
    (e: BeforeUnloadEvent) => {
      const hasUnsavedChanges = description; // if description is present, then there are unsaved changes

      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = true;
      }
    },
    [description],
  );

  const onCreateReasonCallback = (opts: CreateReasonSuccessCallback) => {
    setIsCreateReasonSuccessModalOpen(true);

    if (onAfterCreateReason) {
      onAfterCreateReason(opts);
    }

    onClose();
  };

  const renderReasonDefinitionsLoadingState = () => {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center">
        <Stack spacing={2}>
          <Stack>
            <Typography>
              Hang in there! We are curating Reason Definitions ✨
            </Typography>
            <Typography variant="caption" color="text.secondary">
              (This can take up to 30 seconds)
            </Typography>
          </Stack>
          <LinearProgress variant="indeterminate" />
        </Stack>
      </Stack>
    );
  };

  useEffect(() => {
    window.addEventListener('beforeunload', warnBeforeQuitting);

    return () => {
      window.removeEventListener('beforeunload', warnBeforeQuitting);
    };
  }, [warnBeforeQuitting]);

  useEffect(() => {
    if (open && selectedRC && !selectedReasonDefinition) {
      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          description: `Feedback where users are talking about ${selectedRC.name}`,
          misc: {
            selectedReasonDefinition: selectedRC,
          },
        },
      });
    }
  }, [open, dispatch, selectedRC, selectedReasonDefinition]);

  useEffect(() => {
    if (open && feedbackID && !selectedFeedbackRecordID) {
      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          misc: {
            selectedFeedbackRecordID: feedbackID,
          },
        },
      });
    }
  }, [open, dispatch, feedbackID, selectedFeedbackRecordID]);

  useEffect(() => {
    if (open && onCreateReason && !onAfterCreateReason) {
      dispatch({
        type: TaxonomyActionType.SET_CREATE_REASONV3_STATE,
        payload: {
          misc: {
            onAfterCreateReason: onCreateReason,
          },
        },
      });
    }
  }, [open, dispatch, onCreateReason, onAfterCreateReason]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="lg"
        TransitionComponent={CreateNewReasonModalTransitionComponent}
        PaperProps={{ elevation: 3 }}
        {...modalProps}
      >
        <ModalHeader
          title="Create new Reason"
          onClose={onMinimizeClick}
          rightIcon={
            <Tooltip title="Minimize" placement="top">
              <CloseFullscreenRoundedIcon color="primary" />
            </Tooltip>
          }
        />
        <Divider />
        <DialogContent
          sx={{
            height: '550px',
            paddingBottom: 0,
            paddingTop: activeStep === 2 ? 0 : 2,
            paddingX: activeStep === 2 ? 0 : 2,
            overflow: 'hidden',
          }}
          dividers
        >
          {activeStep === 1 && (
            <Stack height="100%">
              {isFetchingReasonDefinitions ? (
                renderReasonDefinitionsLoadingState()
              ) : (
                <>
                  <Stack mt={2}>
                    <ReasonDescriptionInput
                      description={description}
                      onDescriptionChange={changeDescription}
                    />
                  </Stack>
                  <Stack sx={{ mt: 'auto' }}>
                    <Typography variant="subtitle2">
                      Here are some examples of prompts for Reasons:
                    </Typography>
                    <ReasonDescriptionGuide />
                  </Stack>
                </>
              )}
            </Stack>
          )}
          {activeStep === 2 && <ReviewReason />}
        </DialogContent>
        <CreateNewReasonV3ModalFooter
          onCloseDialog={closeDialogAndResetState}
          onCreateReason={onCreateReasonCallback}
        />
      </Dialog>
      <CreateNewReasonSuccessModal
        open={isCreateReasonSuccessModalOpen}
        onClose={() => setIsCreateReasonSuccessModalOpen(false)}
      />
    </>
  );
};
